import React from 'react';
import { Search, RotateCcw } from 'lucide-react';
import {
  Checkbox,
  Input,
  Button,
  VirtualizedMultiSelectCombobox,
} from '@roger/ui-components';
import { useShallow } from 'zustand/react/shallow';
import type { BillingTableQuery } from '../../hooks/useBillingTableQuery';
import type { BillingReadinessCategory } from '../../services/api/types.gen';
import { useStore } from '../../store';
import { ClinicMultiSelect } from '../clinic/ClinicMultiSelect';
import { useEOEClaimsManagerOptions } from './EOEClaimsManagerContext';

/**
 * The fixed five-category failure grouping, in the order the product contract
 * lists them. Values are the API wire values of `BillingReadinessCategory`.
 */
const FAILING_CATEGORY_OPTIONS: {
  value: BillingReadinessCategory;
  label: string;
}[] = [
  { value: 'oasis', label: 'OASIS' },
  { value: 'hipps', label: 'HIPPS' },
  { value: 'visits', label: 'Visits' },
  { value: 'orders', label: 'Orders' },
  { value: 'insurance', label: 'Insurance' },
];

interface Props {
  query: BillingTableQuery;
  onReset: () => void;
  /** Render the Not Ready failure-category filter. Off for every other tab. */
  showFailingCategories?: boolean;
}

export function BillingTableToolbar({
  query,
  onReset,
  showFailingCategories = false,
}: Props) {
  const { insurances, clinics } = useEOEClaimsManagerOptions();
  const {
    agencyClinics,
    fetchAgencyClinics,
    hideTestPatients,
    setHideTestPatients,
    showInactiveEpisodes,
    setShowInactiveEpisodes,
    hideImportedPatients,
    setHideImportedPatients,
  } = useStore(
    useShallow(state => ({
      agencyClinics: state.agencyClinics,
      fetchAgencyClinics: state.fetchAgencyClinics,
      // Page-wide (not per-tab): every tab's toolbar reads the same slice,
      // so toggling it here carries to the other tabs, and the choice
      // persists through the store's `persist` middleware.
      hideTestPatients: state.hideTestPatients,
      setHideTestPatients: state.setHideTestPatients,
      showInactiveEpisodes: state.showInactiveEpisodes,
      setShowInactiveEpisodes: state.setShowInactiveEpisodes,
      hideImportedPatients: state.hideImportedPatients,
      setHideImportedPatients: state.setHideImportedPatients,
    }))
  );

  React.useEffect(() => {
    if (agencyClinics.length === 0) {
      void fetchAgencyClinics();
    }
  }, [agencyClinics.length, fetchAgencyClinics]);

  const clinicSelectClinics = React.useMemo(
    () =>
      clinics.map(c => {
        const full = agencyClinics.find(ac => ac.id === c.id);
        return {
          id: c.id,
          name: c.name,
          clinic_source: full?.clinic_source,
        };
      }),
    [clinics, agencyClinics]
  );

  // Local input state keeps typing responsive; the committed search is debounced
  // (~250ms) so each keystroke does not trigger a server round-trip.
  const [searchValue, setSearchValue] = React.useState(query.search);

  React.useEffect(() => {
    setSearchValue(query.search);
  }, [query.search]);

  React.useEffect(() => {
    if (searchValue === query.search) return;
    const handle = setTimeout(() => query.setSearch(searchValue), 250);
    return () => clearTimeout(handle);
  }, [searchValue, query.search, query.setSearch]);

  return (
    <div className='mb-3 grid grid-cols-2 gap-2 md:flex md:flex-wrap md:items-center'>
      <div className='relative col-span-2 md:col-auto'>
        <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
        <Input
          inputSize='lg'
          placeholder='Search…'
          value={searchValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchValue(e.target.value)
          }
          className='w-full md:w-72 pl-9'
        />
      </div>

      <VirtualizedMultiSelectCombobox
        options={insurances.map(i => ({ value: i.id, label: i.name }))}
        value={query.insuranceIds}
        onValueChange={query.setInsuranceIds}
        placeholder='All Insurances'
        searchPlaceholder='Search insurances…'
        buttonClassName='h-9 w-full md:w-48'
      />
      <ClinicMultiSelect
        clinics={clinicSelectClinics}
        value={query.clinicIds}
        onValueChange={query.setClinicIds}
        placeholder='All Clinics'
        searchPlaceholder='Search clinics…'
        buttonClassName='h-9 w-full md:w-48'
      />

      {showFailingCategories ? (
        <div className='col-span-2 flex min-w-0 items-center gap-2 md:col-auto'>
          <span className='whitespace-nowrap text-sm font-medium text-muted-foreground'>
            Failing categories
          </span>
          <div className='min-w-0 flex-1 md:flex-none'>
            <VirtualizedMultiSelectCombobox
              options={FAILING_CATEGORY_OPTIONS}
              value={query.failingCategories}
              // The combobox emits plain strings; its options are fixed to the five
              // category wire values, so every emitted value is a valid category.
              onValueChange={values =>
                query.setFailingCategories(values as BillingReadinessCategory[])
              }
              ariaLabel='Failing categories'
              placeholder='Any failing category'
              searchPlaceholder='Search categories…'
              buttonClassName='h-9 w-full md:w-48'
            />
          </div>
        </div>
      ) : null}
      <div className='flex items-center gap-2 col-span-2 md:col-auto'>
        <Checkbox
          id='billing-hide-test-patients'
          checked={hideTestPatients}
          onCheckedChange={(checked: boolean) => {
            setHideTestPatients(!!checked);
          }}
          checkboxSize='sm'
        />
        <label
          htmlFor='billing-hide-test-patients'
          className='text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap'
        >
          Hide test patients
        </label>
      </div>
      <div className='flex items-center gap-2 col-span-2 md:col-auto'>
        <Checkbox
          id='billing-show-inactive-episodes'
          checked={showInactiveEpisodes}
          onCheckedChange={(checked: boolean) => {
            setShowInactiveEpisodes(!!checked);
          }}
          checkboxSize='sm'
        />
        <label
          htmlFor='billing-show-inactive-episodes'
          className='text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap'
        >
          Show inactive episodes
        </label>
      </div>
      <div className='flex items-center gap-2 col-span-2 md:col-auto'>
        <Checkbox
          id='billing-hide-imported-patients'
          checked={hideImportedPatients}
          onCheckedChange={(checked: boolean) => {
            setHideImportedPatients(!!checked);
          }}
          checkboxSize='sm'
        />
        <label
          htmlFor='billing-hide-imported-patients'
          className='text-xs text-muted-foreground cursor-pointer select-none whitespace-nowrap'
        >
          Filter out imported patients
        </label>
      </div>

      <Button
        variant='ghost'
        size='sm'
        onClick={() => {
          // Clear the local input so a pending debounced search cannot re-apply
          // after reset (searchValue === '' === query.search => no reschedule).
          setSearchValue('');
          onReset();
        }}
        className='col-span-2 justify-self-start text-muted-foreground hover:text-foreground md:col-auto'
      >
        <RotateCcw className='mr-1 h-3 w-3' />
        Reset
      </Button>
    </div>
  );
}
